import { useState } from 'react'
import { Modal } from '@wiggui/ds/Modal'
import { GoogleIcon } from '@wiggui/ds/GoogleIcon'
import { Button } from '@wiggui/ds/Button'
import { Text } from '@wiggui/ds/Text'
import { openPDFFromUrl } from '@wiggot/utils'
import { OptionsSwitch } from '../../../options-switch'
import { generateBufferPdf } from '../../../../services'
import { matchAndTranslateErrorMessage } from '../../../../../share/src/helpers/index'

const ModalDownloadPdf = ({
  showModal,
  setShowModal,
  showModalShareFile,
  propertyId,
  propertyType,
  dataPrice,
  legacyId,
  neighborhood
}: {
  showModal: boolean
  setShowModal(showModal: boolean): void
  showModalShareFile: boolean
  propertyId: string
  propertyType: string
  legacyId: string
  neighborhood: string
  dataPrice: {
    saleActive: boolean | null
    leaseActive: boolean | null
    presaleActive: boolean | null
    salePrice: number | null
    leasePrice: number | null
    presalePrice: number | null
  }
}) => {
  const [messageError, setMessageError] = useState<boolean | string>(false)
  const [withBroker, setWithBroker] = useState('true')
  const [isLoadingPdf, setIsLoadingPdf] = useState(false)
  const options = [
    {
      label: (
        <div className="flex items-center gap-2">
          <GoogleIcon name="verified" />
          <Text className="text-sm font-normal">Sí, agregar</Text>
        </div>
      ),
      value: 'true'
    },
    {
      label: (
        <div className="flex items-center gap-2">
          <GoogleIcon name="not_interested" />
          <Text className="text-sm font-normal">No, gracias</Text>
        </div>
      ),
      value: 'false'
    }
  ]

  const handleCancelDownload = () => {
    if (!isLoadingPdf) {
      setShowModal(false)
    }
  }

  const onGenerateOriginalPdf = async () => {
    setIsLoadingPdf(true)
    try {
      const response = await generateBufferPdf({
        propertyId,
        asesorInformation: withBroker === 'true'
      })
      setMessageError(false)

      if (response?.isSuccess && response?.data) {
        try {
          await openPDFFromUrl(response.data.url, response.data.pdfName)
          setShowModal(false)
        } catch {
          setMessageError(
            'No se pudo descargar el PDF. La URL pudo haber expirado; intenta de nuevo.'
          )
        }
      } else {
        const catchMessageError =
          response?.error?.errors?.[0]?.message || response?.error?.message

        const normalizeMessageError = catchMessageError
          ?.toString()
          ?.replaceAll('/', '')

        const errorMessage = matchAndTranslateErrorMessage(
          normalizeMessageError
        )
        setMessageError(errorMessage)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingPdf(false)
    }
  }

  return (
    <Modal
      isOpen={showModal && showModalShareFile}
      onClose={() => {}}
      className="w-[552px]"
      isResponsive
      enableBgBlur
    >
      <Modal.Header className="flex justify-between items-center w-full border-b px-6 pt-5 pb-3">
        <p className="text-body-bold-l text-primary font-semibold">
          Descargar PDF
        </p>
        <button
          type="button"
          className="hover:bg-surface-gray-10 rounded-full p-3 flex items-center justify-center active:bg-surface-gray-20 transition-colors duration-300 ease-in-out"
          onClick={() => {
            handleCancelDownload()
          }}
        >
          <GoogleIcon
            name="close"
            className="text-[24px] w-[24px] h-[24px] text-primary"
          />
        </button>
      </Modal.Header>
      <Modal.Body className="w-full p-6 flex flex-col gap-6 justify-center items-center">
        <Text className="text-body-m text-primary font-normal">
          ¿Deseas agregar tus datos de asesor?
        </Text>
        <OptionsSwitch
          className="mr-0 md:mr-5 lg:mr-9 xl:mr-0"
          activeOption={withBroker}
          onChange={(value) => setWithBroker(value)}
          options={options}
        />
        <Text className="text-body-m text-secondary">
          {withBroker === 'true'
            ? 'Mostraremos tus datos en la ficha'
            : 'No mostraremos tus datos en la ficha'}
        </Text>
        {messageError && (
          <Text className="text-body-m text-notif-red">{messageError}</Text>
        )}
      </Modal.Body>
      <Modal.Footer className="flex justify-between items-center w-full border-t px-6 py-2 gap-2">
        <Button
          variant="terciary"
          fullWidth
          onClick={() => {
            handleCancelDownload()
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="secondary"
          isLoading={isLoadingPdf}
          fullWidth
          onClick={onGenerateOriginalPdf}
        >
          Descargar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ModalDownloadPdf
